import React, { useEffect, useRef, useState } from 'react';

/**
 * Animated donut with up to three segments (sharp classic edges, thick ring).
 *  - Principal (blue gradient)
 *  - Interest  (coral gradient)
 *  - Moratorium interest capitalised (amber gradient) — only when > 0
 */
export default function DonutChart({ principal, interest, moratoriumInterest = 0, size = 310, chartId }) {
  const p = Math.max(0, Number(principal) || 0);
  const i = Math.max(0, Number(interest) || 0);
  const m = Math.max(0, Number(moratoriumInterest) || 0);
  const total = Math.max(1, p + i + m);

  const principalPct = (p / total) * 100;
  const interestPct = (i / total) * 100;
  const moratoriumPct = (m / total) * 100;

  const [animPct, setAnimPct] = useState(principalPct);
  const fromRef = useRef(principalPct);
  const rafRef = useRef(0);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const duration = 500;
    const from = fromRef.current;
    const to = principalPct;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = from + (to - from) * eased;
      fromRef.current = next;
      setAnimPct(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [principalPct]);

  // Thicker classic donut geometry
  const radius = 88;
  const stroke = 52;
  const circumference = 2 * Math.PI * radius;

  const principalLen = (principalPct / 100) * circumference;
  const interestLen = (interestPct / 100) * circumference;
  const moratoriumLen = (moratoriumPct / 100) * circumference;

  const principalOffset = 0;
  const interestOffset = -principalLen;
  const moratoriumOffset = -(principalLen + interestLen);

  return (
    <div id={chartId} className="relative donut-3d" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 260 260" className="-rotate-90">
        <defs>
          <linearGradient id="donut-principal" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#38BDF8" />
            <stop offset="55%" stopColor="#0176D3" />
            <stop offset="100%" stopColor="#014486" />
          </linearGradient>
          <linearGradient id="donut-interest" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FCA5A5" />
            <stop offset="55%" stopColor="#F97066" />
            <stop offset="100%" stopColor="#DC2626" />
          </linearGradient>
          <linearGradient id="donut-moratorium" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FCD34D" />
            <stop offset="55%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#B45309" />
          </linearGradient>
        </defs>

        {/* Track */}
        <circle cx="130" cy="130" r={radius} fill="none" stroke="#E2E8F0" strokeWidth={stroke} opacity="0.35" />

        {/* Interest — sharp butt caps */}
        <circle
          cx="130" cy="130" r={radius}
          fill="none"
          stroke="url(#donut-interest)"
          strokeWidth={stroke}
          strokeLinecap="butt"
          strokeDasharray={`${interestLen} ${circumference - interestLen}`}
          strokeDashoffset={interestOffset}
          style={{ transition: 'stroke-dasharray 200ms linear, stroke-dashoffset 200ms linear' }}
        />
        {m > 0 && (
          <circle
            cx="130" cy="130" r={radius}
            fill="none"
            stroke="url(#donut-moratorium)"
            strokeWidth={stroke}
            strokeLinecap="butt"
            strokeDasharray={`${moratoriumLen} ${circumference - moratoriumLen}`}
            strokeDashoffset={moratoriumOffset}
            style={{ transition: 'stroke-dasharray 200ms linear, stroke-dashoffset 200ms linear' }}
          />
        )}
        <circle
          cx="130" cy="130" r={radius}
          fill="none"
          stroke="url(#donut-principal)"
          strokeWidth={stroke}
          strokeLinecap="butt"
          strokeDasharray={`${principalLen} ${circumference - principalLen}`}
          strokeDashoffset={principalOffset}
          style={{ transition: 'stroke-dasharray 120ms linear' }}
        />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xs text-slate-500 dark:text-slate-400">Principal</span>
        <span className="text-xl font-bold text-[#032D60] dark:text-slate-100 tabular-nums">
          {animPct.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}
